import type { ILocationBase } from '~/client/graph'
import { SaveSensorsDocument } from '~/client/graph/operations/generated/Sensors.generated'
import type { ISensorInput } from '~/client/graph/types.generated'
import { LocationType } from '~/client/graph/types.generated'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import {
  compactResourceData,
  mergeResourceData,
  splitResourceFormData,
} from '~/client/src/shared/hooks/splitResourceFormData'

import type {
  ProjectResourceFailure,
  ProjectResourceType,
  ResourceSaveCommand,
} from './projectResourceType'
import type { ResourceResult } from './resourceResult'
import { resourceErr, resourceOk } from './resourceResult'
import {
  parseSensorResource,
  toSensorResourceRecord,
  type MissingSensorResourceId,
  type SensorResource,
} from './sensorResource'

const SENSOR_DEFAULTS: Pick<ISensorInput, 'color' | 'iconName'> = {
  color: '#808080',
  iconName: 'sensor',
}

export interface MissingSensorResourceRecord {
  readonly _tag: 'MissingSensorResourceRecord'
  readonly recordId: string
}

export interface MissingSensorInfuseId {
  readonly _tag: 'MissingSensorInfuseId'
}

export interface SensorInfuseIdContainsSpaces {
  readonly _tag: 'SensorInfuseIdContainsSpaces'
}

export type SensorResourceDomainFailure =
  | MissingSensorResourceId
  | MissingSensorResourceRecord
  | MissingSensorInfuseId
  | SensorInfuseIdContainsSpaces

export type SensorResourceFailure =
  ProjectResourceFailure<SensorResourceDomainFailure>

export function decodeSensorResources(
  locations: readonly ILocationBase[],
): ResourceResult<readonly SensorResource[], SensorResourceDomainFailure> {
  const resources: SensorResource[] = []

  for (const location of locations) {
    if (location.type !== LocationType.Sensor || location.isDeleted) continue

    const resource = parseSensorResource(location)
    if (resource._tag === 'Err') return resource
    resources.push(resource.value)
  }

  return resourceOk(resources)
}

const sensorInputOf = (
  resource: SensorResource,
  projectId: string,
  recordId: string,
  rootFields: Readonly<Record<string, unknown>>,
  dataFields: Readonly<Record<string, unknown>>,
  templateId: string | null | undefined,
): ISensorInput => ({
  assignedGlobes: [...resource.assignedGlobes],
  assignedSitemaps: [...resource.assignedSitemaps],
  code: resource.code,
  color: resource.color,
  data: mergeResourceData({ ...resource.data }, { ...dataFields }),
  iconName: resource.iconName,
  id: recordId,
  infuseId: resource.infuseId,
  maxBookingDuration: resource.maxBookingDuration,
  maxOverlappingDeliveries: resource.maxOverlappingDeliveries,
  name: typeof rootFields.name === 'string' ? rootFields.name : resource.name,
  operatingIntervals: resource.operatingIntervals.map(interval => ({
    ...interval,
  })),
  // By id only: a parentType the resource still carries is never sent (STX1-1355).
  parent: resource.parent ? { parentId: resource.parent.parentId } : null,
  permittedCompanies: [...resource.permittedCompanies],
  projectId,
  templateId: templateId ?? resource.templateId,
})

export function buildSensorSaveVariables(
  projectId: string,
  command: ResourceSaveCommand,
  resources: readonly SensorResource[],
): ResourceResult<
  { readonly sensors: readonly ISensorInput[] },
  SensorResourceDomainFailure
> {
  const { recordId, formData } = command
  const { templateId, rootFields, dataFields } = splitResourceFormData(
    ResourceTypeId.SENSORS,
    { ...formData },
    ['infuseId'],
  )
  const isCreation = recordId === 'new' || !recordId
  const existing = isCreation
    ? null
    : (resources.find(resource => resource.id === recordId) ?? null)

  if (!isCreation && !existing) {
    return resourceErr({
      _tag: 'MissingSensorResourceRecord',
      recordId,
    })
  }

  if (existing) {
    return resourceOk({
      sensors: [
        sensorInputOf(
          existing,
          projectId,
          recordId,
          rootFields,
          dataFields,
          templateId,
        ),
      ],
    })
  }

  const infuseId =
    typeof rootFields.infuseId === 'string' ? rootFields.infuseId.trim() : ''
  if (!infuseId) return resourceErr({ _tag: 'MissingSensorInfuseId' })
  if (infuseId.includes(' ')) {
    return resourceErr({ _tag: 'SensorInfuseIdContainsSpaces' })
  }

  const name = typeof rootFields.name === 'string' ? rootFields.name.trim() : ''

  return resourceOk({
    sensors: [
      {
        ...SENSOR_DEFAULTS,
        projectId,
        infuseId,
        name: name || infuseId,
        templateId: templateId ?? null,
        data: compactResourceData({ ...dataFields }),
      },
    ],
  })
}

export const sensorResourceType: ProjectResourceType<
  SensorResource,
  SensorResourceDomainFailure
> = {
  typeId: ResourceTypeId.SENSORS,
  saveDocument: SaveSensorsDocument,
  decodeResources: decodeSensorResources,
  toRecord: toSensorResourceRecord,
  buildSaveVariables: buildSensorSaveVariables,
}
